import { useState, useEffect } from 'react';
import { intakesAPI } from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, ClipboardList, MoreVertical, Edit, Trash2, Loader2, ArrowRight, Mail, Phone, CalendarDays } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { ScrollArea } from '../components/ui/scroll-area';
import { format } from 'date-fns';

export default function Intakes() {
  const { t } = useLanguage();
  
  const STAGES = [
    { key: 'new_intake', label: t('newIntake'), color: 'bg-[#78866B]', bgLight: 'bg-[#EDF1EB]' },
    { key: 'consultation', label: t('consultation'), color: 'bg-yellow-500', bgLight: 'bg-yellow-50' },
    { key: 'offer_sent', label: t('offerSent'), color: 'bg-blue-500', bgLight: 'bg-blue-50' },
    { key: 'offer_accepted', label: t('offerAccepted'), color: 'bg-[#A8A29E]', bgLight: 'bg-[#F5F5F4]' }
  ];

  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIntake, setEditingIntake] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    client_name: '',
    email: '',
    phone: '',
    service_type: '',
    notes: '',
    stage: 'new_intake',
    intake_date: format(new Date(), 'yyyy-MM-dd'),
    follow_up_date: ''
  });

  const fetchIntakes = async () => {
    try {
      const res = await intakesAPI.getAll();
      setIntakes(res.data);
    } catch (error) {
      toast.error('Failed to fetch intakes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntakes();
  }, []);

  const resetForm = () => {
    setFormData({
      client_name: '',
      email: '',
      phone: '',
      service_type: '',
      notes: '',
      stage: 'new_intake',
      intake_date: format(new Date(), 'yyyy-MM-dd'),
      follow_up_date: ''
    });
    setEditingIntake(null);
  };

  const openAddDialog = (stage = 'new_intake') => {
    resetForm();
    setFormData(prev => ({ ...prev, stage }));
    setDialogOpen(true);
  };

  const openEditDialog = (intake) => {
    setFormData({
      client_name: intake.client_name || '',
      email: intake.email || '',
      phone: intake.phone || '',
      service_type: intake.service_type || '',
      notes: intake.notes || '',
      stage: intake.stage || 'new_intake',
      intake_date: intake.intake_date || '',
      follow_up_date: intake.follow_up_date || ''
    });
    setEditingIntake(intake);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_name.trim()) {
      toast.error(t('clientName') + ' is required');
      return;
    }
    if (!formData.email.trim()) {
      toast.error(t('email') + ' is required');
      return;
    }
    if (!formData.stage) {
      toast.error(t('stage') + ' is required');
      return;
    }

    setSaving(true);
    try {
      if (editingIntake) {
        await intakesAPI.update(editingIntake.id, formData);
        toast.success(t('intakeUpdated'));
      } else {
        await intakesAPI.create(formData);
        toast.success(t('intakeCreated'));
      }
      setDialogOpen(false);
      resetForm();
      fetchIntakes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save intake');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (intake) => {
    if (!window.confirm(`${t('delete')} ${intake.client_name}?`)) return;
    
    try {
      await intakesAPI.delete(intake.id);
      toast.success(t('intakeDeleted'));
      fetchIntakes();
    } catch (error) {
      toast.error('Failed to delete intake');
    }
  };

  const handleStageChange = async (intake, newStage) => {
    try {
      await intakesAPI.update(intake.id, { stage: newStage });
      const stageName = STAGES.find(s => s.key === newStage)?.label;
      toast.success(`${t('moveTo')} ${stageName}`);
      fetchIntakes();
    } catch (error) {
      toast.error('Failed to update stage');
    }
  };

  const getIntakesByStage = (stage) => {
    return intakes.filter(i => i.stage === stage);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 w-48 bg-[#E7E5E4] rounded-lg animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-96 bg-[#E7E5E4] rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="intakes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-medium text-[#292524] tracking-tight">{t('intakeOverview')}</h1>
          <p className="text-[#78716C] mt-1">{intakes.length} {t('totalIntakesCount')}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="rounded-full bg-[#57534E] hover:bg-[#57534E]/90 h-11 px-6"
              onClick={() => openAddDialog()}
              data-testid="add-intake-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('newIntake')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-medium">
                {editingIntake ? t('editIntake') : t('newIntake')}
              </DialogTitle>
              <DialogDescription className="sr-only">Intake form</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="client_name">{t('clientName')} *</Label>
                <Input
                  id="client_name"
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  placeholder={t('clientName')}
                  className="h-11"
                  required
                  data-testid="intake-client-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('email')} *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@example.com"
                    className="h-11"
                    required
                    data-testid="intake-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">{t('phone')}</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="h-11"
                    data-testid="intake-phone-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="intake_date">{t('intakeDate')}</Label>
                  <Input
                    id="intake_date"
                    type="date"
                    value={formData.intake_date}
                    onChange={(e) => setFormData({ ...formData, intake_date: e.target.value })}
                    className="h-11"
                    data-testid="intake-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="follow_up_date">{t('followUpDate')}</Label>
                  <Input
                    id="follow_up_date"
                    type="date"
                    value={formData.follow_up_date}
                    onChange={(e) => setFormData({ ...formData, follow_up_date: e.target.value })}
                    className="h-11"
                    data-testid="intake-followup-date-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="service_type">{t('serviceType')}</Label>
                <Input
                  id="service_type"
                  value={formData.service_type}
                  onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                  placeholder={t('serviceType')}
                  className="h-11"
                  data-testid="intake-service-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="stage">{t('stage')} *</Label>
                <Select
                  value={formData.stage}
                  onValueChange={(value) => setFormData({ ...formData, stage: value })}
                >
                  <SelectTrigger className="h-11" data-testid="intake-stage-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STAGES.map((stage) => (
                      <SelectItem key={stage.key} value={stage.key}>
                        {stage.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">{t('notes')}</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder={t('notes')}
                  rows={3}
                  data-testid="intake-notes-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">
                  {t('cancel')}
                </Button>
                <Button 
                  type="submit" 
                  className="rounded-full bg-[#57534E] hover:bg-[#57534E]/90"
                  disabled={saving}
                  data-testid="save-intake-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingIntake ? t('update') : t('create')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Kanban Board */}
      <div className="overflow-x-auto pb-4 md:overflow-visible">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:min-w-[800px]">
          {STAGES.map((stage) => {
            const stageIntakes = getIntakesByStage(stage.key);
            const stageIndex = STAGES.findIndex(s => s.key === stage.key);
            const nextStage = STAGES[stageIndex + 1];
            
            return (
              <div key={stage.key} className="space-y-4" data-testid={`stage-${stage.key}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${stage.color}`} />
                    <h3 className="font-medium text-[#292524]">{stage.label}</h3>
                    <span className="text-sm text-[#78716C] bg-[#F5F5F4] px-2 py-0.5 rounded-full">
                      {stageIntakes.length}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => openAddDialog(stage.key)}
                    data-testid={`add-to-${stage.key}-btn`}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                
                <ScrollArea className="h-[calc(100vh-280px)]">
                  <div className="space-y-3 pr-2">
                    {stageIntakes.length === 0 ? (
                      <div className={`${stage.bgLight} rounded-xl p-6 text-center border-2 border-dashed border-[#E7E5E4]`}>
                        <ClipboardList className="w-8 h-8 text-[#A8A29E] mx-auto mb-2" />
                        <p className="text-sm text-[#78716C]">{t('noIntakes')}</p>
                      </div>
                    ) : (
                      stageIntakes.map((intake) => (
                        <Card 
                          key={intake.id}
                          className="border-0 shadow-sm hover:shadow-md transition-all duration-200 overflow-visible"
                          data-testid={`intake-card-${intake.id}`}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-medium text-[#292524] pr-2">{intake.client_name}</h4>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" data-testid={`intake-menu-${intake.id}`}>
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" side="bottom" className="z-50">
                                  <DropdownMenuItem onClick={() => openEditDialog(intake)} data-testid={`edit-intake-${intake.id}`}>
                                    <Edit className="w-4 h-4 mr-2" />
                                    {t('edit')}
                                  </DropdownMenuItem>
                                  {nextStage && (
                                    <DropdownMenuItem onClick={() => handleStageChange(intake, nextStage.key)} data-testid={`move-intake-${intake.id}`}>
                                      <ArrowRight className="w-4 h-4 mr-2" />
                                      {t('moveTo')} {nextStage.label}
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem 
                                    onClick={() => handleDelete(intake)}
                                    className="text-red-600"
                                    data-testid={`delete-intake-${intake.id}`}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    {t('delete')}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                            
                            <div className="space-y-1 text-sm text-[#78716C]">
                              {intake.service_type && (
                                <p className="text-xs bg-[#F5F5F4] px-2 py-1 rounded-full inline-block">
                                  {intake.service_type}
                                </p>
                              )}
                              {intake.email && (
                                <div className="flex items-center gap-1">
                                  <Mail className="w-3 h-3 shrink-0" />
                                  <span className="truncate">{intake.email}</span>
                                </div>
                              )}
                              {intake.phone && (
                                <div className="flex items-center gap-1">
                                  <Phone className="w-3 h-3 shrink-0" />
                                  <span>{intake.phone}</span>
                                </div>
                              )}
                              {(intake.intake_date || intake.follow_up_date) && (
                                <div className="flex flex-col gap-0.5 mt-1">
                                  {intake.intake_date && (
                                    <div className="flex items-center gap-1 text-xs">
                                      <CalendarDays className="w-3 h-3 shrink-0" />
                                      <span>{format(new Date(intake.intake_date), 'MMM d, yyyy')}</span>
                                    </div>
                                  )}
                                  {intake.follow_up_date && (
                                    <div className="flex items-center gap-1 text-xs text-[#78866B]">
                                      <CalendarDays className="w-3 h-3 shrink-0" />
                                      <span>{t('followUp')}: {format(new Date(intake.follow_up_date), 'MMM d, yyyy')}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                              {intake.notes && (
                                <p className="text-xs line-clamp-2 mt-2 pt-2 border-t border-[#E7E5E4]">
                                  {intake.notes}
                                </p>
                              )}
                              {!intake.intake_date && intake.created_at && (
                                <div className="flex items-center gap-1 mt-2 pt-2 border-t border-[#E7E5E4] text-xs text-[#A8A29E]">
                                  <CalendarDays className="w-3 h-3" />
                                  <span>{format(new Date(intake.created_at), 'MMM d, yyyy')}</span>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
